/**
 * useDevicePermissions Hook - New Implementation
 * Wraps the new useMediaPermissions system while maintaining backward compatibility
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { useMediaPermissions } from 'src/customHooks/mediaPermissions'
import { PERMISSION_STATES } from 'src/services/mediaPermissions'
import { checkAvailableDevices } from 'src/live/shell/devicePermissions/checkAvailableDevices'
import { 
  getAndSetCameras, 
  getAndSetMicrophones, 
  getAndSetSpeakers 
} from '../LiveSessionPageComponents'

export const useDevicePermissions = (options = {}) => {
  const { skipAutoCheck = false } = options
  
  // Use the new media permissions system
  // Skip autoCheck if this is for an offline session
  // IMPORTANT: autoCheck is reactive - if skipAutoCheck changes, autoCheck will change
  const mediaPermissions = useMediaPermissions({
    mediaType: 'both',
    autoCheck: !skipAutoCheck, // Don't auto-check if skipAutoCheck is true
    sessionType: 'liveSession',
  })

  // Backward compatible states
  const [microphoneError, setMicrophoneError] = useState(true)
  const [cameraError, setCameraError] = useState(true)
  
  // Dialog states - now managed by new system but exposed for compatibility
  const [giveBothPermissionsDialogOpen, setGiveBothPermissionsDialogOpen] = useState(false)
  const [giveCameraPermissionDialogOpen, setGiveCameraPermissionDialogOpen] = useState(false)
  const [giveMicrophonePermissionDialogOpen, setGiveMicrophonePermissionDialogOpen] = useState(false)
  
  // User permission states - Default to OFF (false) so mic and camera are off by default
  const [userHasAllowedAudio, setUserHasAllowedAudio] = useState(false)
  const [userHasAllowedVideo, setUserHasAllowedVideo] = useState(false)
  
  // Available devices
  const [availableCameras, setAvailableCameras] = useState([])
  const [selectedCamera, setSelectedCamera] = useState()
  const [availableMicrophones, setAvailableMicrophones] = useState([])
  const [selectedMicrophone, setSelectedMicrophone] = useState()
  const [availableSpeakers, setAvailableSpeakers] = useState([])
  const [selectedSpeaker, setSelectedSpeaker] = useState()

  // Refs to track if devices have been loaded to prevent infinite loops
  const camerasLoadedRef = useRef(false)
  const microphonesLoadedRef = useRef(false)
  const isLoadingDevicesRef = useRef(false)
  const lastCameraPermissionRef = useRef(null)
  const lastMicrophonePermissionRef = useRef(null)
  const effectRunCountRef = useRef(0)
  const lastEffectRunTimeRef = useRef(0)
  const isCheckingDevicesRef = useRef(false) // Guard to prevent concurrent checkAvailableDevices calls

  // Sync new permission states with old states
  // CRITICAL: Only check/load devices if skipAutoCheck is false (i.e., not an offline session)
  useEffect(() => {
    const now = Date.now()
    const timeSinceLastRun = now - lastEffectRunTimeRef.current
    
    // Reset counter if enough time has passed (debounce)
    if (timeSinceLastRun > 1000) {
      effectRunCountRef.current = 0
    }
    
    effectRunCountRef.current += 1
    lastEffectRunTimeRef.current = now
    
    // Safety check: if effect runs too many times in quick succession, stop
    if (effectRunCountRef.current > 5 && timeSinceLastRun < 100) {
      console.warn('[useDevicePermissions] Effect running too many times, stopping to prevent loop', {
        count: effectRunCountRef.current,
        timeSinceLastRun,
        cameraPermission: mediaPermissions.cameraPermission,
        microphonePermission: mediaPermissions.microphonePermission,
      })
      return
    }
    
    // If auto-check is skipped (offline session), don't load devices
    if (skipAutoCheck) {
      camerasLoadedRef.current = false
      microphonesLoadedRef.current = false
      lastCameraPermissionRef.current = null
      lastMicrophonePermissionRef.current = null
      effectRunCountRef.current = 0
      return
    }
    
    // Prevent concurrent device loading
    if (isLoadingDevicesRef.current) {
      return
    }
    
    const currentCameraPermission = mediaPermissions.cameraPermission
    const currentMicrophonePermission = mediaPermissions.microphonePermission
    const isCameraGranted = currentCameraPermission === PERMISSION_STATES.GRANTED
    const isMicrophoneGranted = currentMicrophonePermission === PERMISSION_STATES.GRANTED
    
    // Only update error states if permission state actually changed
    const cameraPermissionChanged = lastCameraPermissionRef.current !== currentCameraPermission
    const microphonePermissionChanged = lastMicrophonePermissionRef.current !== currentMicrophonePermission
    
    // Only proceed if permissions actually changed
    if (!cameraPermissionChanged && !microphonePermissionChanged) {
      return
    }
    
    if (cameraPermissionChanged) {
      setCameraError(!isCameraGranted)
      lastCameraPermissionRef.current = currentCameraPermission
    }
    
    if (microphonePermissionChanged) {
      setMicrophoneError(!isMicrophoneGranted)
      lastMicrophonePermissionRef.current = currentMicrophonePermission
    }
    
    // Update device lists when permissions are granted (only once per permission grant)
    // Only load if permission is granted AND we haven't loaded yet
    if (isCameraGranted && !camerasLoadedRef.current && !isLoadingDevicesRef.current) {
      isLoadingDevicesRef.current = true
      camerasLoadedRef.current = true
      getAndSetCameras({ setAvailableCameras, setSelectedCamera })
        .then(() => {
          isLoadingDevicesRef.current = false
          effectRunCountRef.current = 0 // Reset counter on success
        })
        .catch((error) => {
          console.error('Error loading cameras:', error)
          camerasLoadedRef.current = false
          isLoadingDevicesRef.current = false
        })
    }
    
    if (isMicrophoneGranted && !microphonesLoadedRef.current && !isLoadingDevicesRef.current) {
      isLoadingDevicesRef.current = true
      microphonesLoadedRef.current = true
      Promise.all([
        getAndSetMicrophones({ setAvailableMicrophones, setSelectedMicrophone }),
        getAndSetSpeakers({ setAvailableSpeakers, setSelectedSpeaker })
      ])
        .then(() => {
          isLoadingDevicesRef.current = false
          effectRunCountRef.current = 0 // Reset counter on success
        })
        .catch((error) => {
          console.error('Error loading microphones/speakers:', error)
          microphonesLoadedRef.current = false
          isLoadingDevicesRef.current = false
        })
    }
    
    // Reset loaded flags if permissions are revoked (but don't reload immediately)
    if (!isCameraGranted && camerasLoadedRef.current) {
      camerasLoadedRef.current = false
    }
    if (!isMicrophoneGranted && microphonesLoadedRef.current) {
      microphonesLoadedRef.current = false
    }
  }, [
    skipAutoCheck,
    mediaPermissions.cameraPermission,
    mediaPermissions.microphonePermission,
    // Removed availableCameras.length and availableMicrophones.length to prevent loops
    // Using refs instead to track loading state
    // setState functions are stable and don't need to be in deps
  ])

  // Sync education/blocked dialogs with old dialog states
  useEffect(() => {
    // If education dialog is shown, map it to "both permissions" dialog
    if (mediaPermissions.showEducation) {
      setGiveBothPermissionsDialogOpen(true)
    } else {
      setGiveBothPermissionsDialogOpen(false)
    }
  }, [mediaPermissions.showEducation])

  // Use refs to access mediaPermissions methods to avoid dependency on the entire object
  const mediaPermissionsRef = useRef(mediaPermissions)
  useEffect(() => {
    mediaPermissionsRef.current = mediaPermissions
  }, [mediaPermissions])

  const checkAvailableDevices = useCallback(async () => {
    // Guard: prevent concurrent calls
    if (isCheckingDevicesRef.current) {
      console.log('[checkAvailableDevices] Already checking, skipping duplicate call')
      return
    }

    isCheckingDevicesRef.current = true
    try {
      // Use the new system's check
      await mediaPermissionsRef.current.checkPermissions()
      
      // Also check using the old method as a fallback to ensure we catch PROMPT state
      // This is important because the new system might not always detect PROMPT correctly
      let oldMethodPrompt = false
      try {
        const permissionToAccessMicrophone = await navigator.permissions.query({ name: 'microphone' })
        const permissionToAccessCamera = await navigator.permissions.query({ name: 'camera' })
        
        // Check if both are in prompt state (old method)
        if (
          permissionToAccessMicrophone.state === 'prompt' &&
          permissionToAccessCamera.state === 'prompt'
        ) {
          oldMethodPrompt = true
        }
      } catch (e) {
        // If permissions.query is not supported, continue with new system
        console.log('Old permission check method not available:', e)
      }
      
      // Get current permission states from ref
      const currentMediaPermissions = mediaPermissionsRef.current
      const isCameraGranted = currentMediaPermissions.cameraPermission === PERMISSION_STATES.GRANTED
      const isMicrophoneGranted = currentMediaPermissions.microphonePermission === PERMISSION_STATES.GRANTED
      
      if (isCameraGranted) {
        getAndSetCameras({ setAvailableCameras, setSelectedCamera })
        setCameraError(false)
      } else {
        setCameraError(true)
      }
      
      if (isMicrophoneGranted) {
        getAndSetMicrophones({ setAvailableMicrophones, setSelectedMicrophone })
        getAndSetSpeakers({ setAvailableSpeakers, setSelectedSpeaker })
        setMicrophoneError(false)
      } else {
        setMicrophoneError(true)
      }
      
      // If permissions are in prompt state (either from new or old method), trigger the dialog
      const isPrompt = 
        currentMediaPermissions.cameraPermission === PERMISSION_STATES.PROMPT ||
        currentMediaPermissions.microphonePermission === PERMISSION_STATES.PROMPT ||
        oldMethodPrompt
        
      if (isPrompt) {
        console.log('[checkAvailableDevices] Permissions in PROMPT state, triggering dialog', {
          cameraPermission: currentMediaPermissions.cameraPermission,
          microphonePermission: currentMediaPermissions.microphonePermission,
          oldMethodPrompt,
        })
        // Trigger the education flow - this sets showEducation to true
        currentMediaPermissions.startPermissionFlow()
        // Also directly set the old dialog open as a fallback
        // This ensures the dialog shows even if the sync effect hasn't run yet
        setGiveBothPermissionsDialogOpen(true)
      } else {
        console.log('[checkAvailableDevices] Permissions not in PROMPT state', {
          cameraPermission: currentMediaPermissions.cameraPermission,
          microphonePermission: currentMediaPermissions.microphonePermission,
          oldMethodPrompt,
        })
      }
    } finally {
      isCheckingDevicesRef.current = false
    }
  }, [
    // Only depend on stable setState functions, not the mediaPermissions object
    setAvailableCameras,
    setSelectedCamera,
    setAvailableMicrophones,
    setSelectedMicrophone,
    setAvailableSpeakers,
    setSelectedSpeaker,
    setCameraError,
    setMicrophoneError,
    setGiveBothPermissionsDialogOpen,
  ])

  return {
    // Permission states (backward compatible)
    microphoneError,
    setMicrophoneError,
    cameraError,
    setCameraError,
    
    // Dialog states (backward compatible)
    giveBothPermissionsDialogOpen,
    setGiveBothPermissionsDialogOpen,
    giveCameraPermissionDialogOpen,
    setGiveCameraPermissionDialogOpen,
    giveMicrophonePermissionDialogOpen,
    setGiveMicrophonePermissionDialogOpen,
    
    // User permission states (backward compatible)
    userHasAllowedAudio,
    setUserHasAllowedAudio,
    userHasAllowedVideo,
    setUserHasAllowedVideo,
    
    // Available devices (backward compatible)
    availableCameras,
    setAvailableCameras,
    selectedCamera,
    setSelectedCamera,
    availableMicrophones,
    setAvailableMicrophones,
    selectedMicrophone,
    setSelectedMicrophone,
    availableSpeakers,
    setAvailableSpeakers,
    selectedSpeaker,
    setSelectedSpeaker,
    
    // Methods (backward compatible)
    checkAvailableDevices,
    
    // New system access (for components that want to use new features)
    mediaPermissions,
  }
}

